import { postData } from '../utils/ajax-utils';
import { uniqid } from '../utils/utils';
import Modal, { ModalHTMLElement } from './modal';

declare global {
    interface ElementEventMap {
        'phone_otp_module_success': CustomEvent<{ otpGuid: string, phone: string }>;
    }
}

export interface PhoneOtpModuleHTMLElement extends HTMLElement {
    phoneOtpModule: PhoneOtpModule;
}

export function initPhoneOtpModule() : void {
    document.querySelectorAll<PhoneOtpModuleHTMLElement>('.js-phone-otp-module').forEach($phoneOtpModule => {
        new PhoneOtpModule($phoneOtpModule);
    });
}

export class PhoneOtpModule {
    newVersion = false;

    id: string;
    $container: PhoneOtpModuleHTMLElement;
    $phoneFieldContainer: HTMLElement|null;
    $phoneField: HTMLInputElement|null;
    $addPhoneFieldButton: HTMLButtonElement|null;
    $editPhoneFieldButton: HTMLButtonElement|null;
    $phoneFieldModal: ModalHTMLElement;
    $phoneCodeFieldForVerification: HTMLSelectElement;
    $phoneFieldForVerification: HTMLInputElement;
    $phoneFieldForVerificationError: HTMLElement;
    $validatePhoneFieldButton: HTMLButtonElement;
    $verificationFieldsModal: ModalHTMLElement;
    $verificationFieldsContainer: HTMLElement;
    $verificationFields: NodeListOf<HTMLInputElement>;
    $verificationFieldsError: HTMLElement;
    $resendCodeButton: HTMLButtonElement;
    $submitButton: HTMLButtonElement|null;
    $otpGuidField: HTMLInputElement|null;
    orderGuid: string|null;
    contactUniqid: string|null = null;
    contactRefExt: string|null = null;
    noUpdate: boolean;
    otpGuid: string|null = null;
    autoValidate = false; // Auto submit after otp code is validated
    resetPhoneFieldOnSuccess = true;

    constructor($container: PhoneOtpModuleHTMLElement) {
        $container.phoneOtpModule = this;

        this.id = uniqid();
        this.$container = $container;
        this.$phoneFieldContainer = this.$container.querySelector<HTMLElement>('.js-phone-otp-module-phone-field-container');
        this.$phoneField = this.$container.querySelector<HTMLInputElement>('.js-phone-otp-module-phone-field');
        this.$addPhoneFieldButton = this.$container.querySelector<HTMLButtonElement>('.js-phone-otp-module-add-phone-field-button');
        this.$editPhoneFieldButton = this.$container.querySelector<HTMLButtonElement>('.js-phone-otp-module-edit-phone-field-button');
        this.$phoneFieldModal = this.$container.querySelector('.js-phone-otp-module-phone-field-modal') as ModalHTMLElement;
        this.$phoneCodeFieldForVerification = this.$container.querySelector('.js-phone-otp-module-phone-code-field-for-verification') as HTMLSelectElement;
        this.$phoneFieldForVerification = this.$container.querySelector('.js-phone-otp-module-phone-field-for-verification') as HTMLInputElement;
        this.$phoneFieldForVerificationError = this.$container.querySelector('.' + this.$phoneFieldForVerification.dataset.errorContainer) as HTMLElement;
        this.$validatePhoneFieldButton = this.$container.querySelector('.js-phone-otp-module-validate-phone-field-button') as HTMLButtonElement;
        this.$verificationFieldsModal = this.$container.querySelector('.js-phone-otp-module-verification-modal') as ModalHTMLElement;
        this.$verificationFieldsContainer = this.$container.querySelector('.js-phone-otp-module-verification-fields-container') as HTMLElement;
        this.$verificationFields = this.$verificationFieldsContainer.querySelectorAll<HTMLInputElement>('.js-phone-otp-module-verification-field');
        this.$verificationFieldsError = this.$verificationFieldsContainer.querySelector('.' + this.$verificationFieldsContainer.dataset.errorContainer) as HTMLElement;
        this.$resendCodeButton = this.$container.querySelector('.js-phone-otp-module-resend-code-button') as HTMLButtonElement;
        this.$submitButton = this.$container.querySelector<HTMLButtonElement>('.js-phone-otp-module-submit');
        this.$otpGuidField = this.$container.querySelector<HTMLInputElement>('.js-phone-otp-module-otp-guid');
        this.orderGuid = this.$container.dataset.orderGuid ?? null;
        this.autoValidate = typeof this.$container.dataset.autoValidate === 'undefined' || this.$container.dataset.autoValidate !== 'false';
        this.noUpdate = typeof this.$container.dataset.noUpdate !== 'undefined' && this.$container.dataset.noUpdate !== 'false';
        this.resetPhoneFieldOnSuccess = typeof this.$container.dataset.resetPhoneFieldOnSuccess !== 'undefined' && this.$container.dataset.resetPhoneFieldOnSuccess !== 'false';

        if (this.$phoneField?.dataset.contactUniqid || this.$phoneFieldForVerification.dataset.contactUniqid) {
            this.contactUniqid = this.$phoneField?.dataset.contactUniqid ?? this.$phoneFieldForVerification.dataset.contactUniqid ?? null;
        }

        if (this.$phoneField?.dataset.contactRefExt || this.$phoneFieldForVerification.dataset.contactRefExt) {
            this.contactRefExt = this.$phoneField?.dataset.contactRefExt ?? this.$phoneFieldForVerification.dataset.contactRefExt ?? null;
        }

        // Form events
        this.$addPhoneFieldButton?.addEventListener('click', () => this.addPhoneField());
        this.$editPhoneFieldButton?.addEventListener('click', () => this.addPhoneField());
        this.$phoneFieldForVerification.addEventListener('keypress', (e) => this.onPhoneFieldForVerificationKeyPress(e));
        this.$validatePhoneFieldButton.addEventListener('click', () => this.validatePhoneField());
        this.$verificationFieldsContainer.addEventListener('keyup', (e) => this.onVerificationFieldsKeyUp(e));
        this.$resendCodeButton.addEventListener('click', () => this.resendOtpCode());
    }

    /**
     * Détermine s'il faut utiliser l'ancienne version (API revendeur) ou la nouvelle (API Dolibarr)
     * 
     * @return {void}
     */
    useNewVersion(): void {
        this.newVersion = true;
    }

    setContactRefExt(contactRefExt: string): void {
        this.contactRefExt = contactRefExt;
    }

    setResetPhoneFieldOnSuccess(resetPhoneFieldOnSuccess: boolean): void {
        this.resetPhoneFieldOnSuccess = resetPhoneFieldOnSuccess;
    }

    /**
     * Open modal asking the phone number
     * 
     * @return {void}
     */
    addPhoneField() : void {
        Modal.openModal(this.$phoneFieldModal);
    }

    /**
     * Validate the phone field when pressing Enter key in phone field
     * 
     * @param {KeyboardEvent} e 
     * 
     * @return {void}
     */
    onPhoneFieldForVerificationKeyPress(e: KeyboardEvent) : void {
        if ('Enter' === e.key) {
            e.preventDefault();
            this.$validatePhoneFieldButton.click();
        }
    }

    /**
     * Validate the phone field and send otp code
     * 
     * @return {void}
     */
    validatePhoneField() : void {
        // Disable button and show loading
        this.$validatePhoneFieldButton.setAttribute('disabled', 'true');
        this.$validatePhoneFieldButton.querySelector('.loading')?.classList.remove('hidden');

        // Remove errors
        this.$phoneFieldForVerificationError.innerHTML = '';
        this.$phoneFieldForVerificationError.closest('.form-group')?.classList.remove('has-error');

        this.sendOtpCode().then(response => {
            // Re-enable button and hide loading
            this.$validatePhoneFieldButton.removeAttribute('disabled');
            this.$validatePhoneFieldButton.querySelector('.loading')?.classList.add('hidden');

            if (response.success) {
                Modal.closeModal(this.$phoneFieldModal);
                Modal.openModal(this.$verificationFieldsModal).then($modal => {
                    // Focus sur le premier champ du code OTP à renseigner
                    setTimeout(() => {
                        this.$verificationFields[0].focus();
                    }, parseFloat(window.getComputedStyle($modal).transitionDuration) * 1000);
                });
            } else {
                // Show errors
                const errorMessage = response.data?.textError || this.$phoneFieldForVerification.dataset.invalidFormatMessage;

                this.$phoneFieldForVerificationError.innerHTML = `<li>${errorMessage}</li>`;
                this.$phoneFieldForVerificationError.closest('.form-group')?.classList.add('has-error');
            }
        }).catch(error => {
            console.error('Error:', error);

            // Re-enable button and hide loading
            this.$validatePhoneFieldButton.removeAttribute('disabled');
            this.$validatePhoneFieldButton.querySelector('.loading')?.classList.add('hidden');

            // Show errors
            this.$phoneFieldForVerificationError.innerHTML = `<li>${this.$phoneFieldForVerification.dataset.unexpectedErrorMessage}</li>`;
            this.$phoneFieldForVerificationError.closest('.form-group')?.classList.add('has-error');
        });
    }

    /**
     * Send the OTP code to phone number
     * 
     * @return {Promise<any>}
     */
    async sendOtpCode() : Promise<any> {
        type SendOtpCodeDataType = {
            action: 'send_otp_code',
            phone: string,
            contactRefExt: string,
        };

        type OldSendOtpCodeDataType = {
            action: 'old_send_otp_code',
            phone: string,
            contactUniqid: string,
            contactRefExt: string,
            orderGuid?: string,
        };

        const selectedOption = this.$phoneCodeFieldForVerification
            .querySelector(`option:nth-child(${this.$phoneCodeFieldForVerification.selectedIndex + 1})`) as HTMLOptionElement;

        const dataToPost: SendOtpCodeDataType|OldSendOtpCodeDataType = this.newVersion ? {
            action: 'send_otp_code',
            phone: '+' + selectedOption.dataset.code + this.$phoneFieldForVerification.value,
            contactRefExt: this.contactRefExt ?? '',
        } : {
            action: 'old_send_otp_code',
            phone: '+' + selectedOption.dataset.code + this.$phoneFieldForVerification.value,
            contactUniqid: this.contactUniqid ?? '',
            contactRefExt: this.contactRefExt ?? '',
            orderGuid: this.orderGuid ?? '',
        };

        return await postData(scriptVar.ajaxUrl, dataToPost).then(response => {
            if (response.success) {
                this.otpGuid = response.data.otpGuid;
            } else {
                this.otpGuid = null;
            }

            return response;
        });
    }

    /**
     * Resend the OTP code to phone number
     * 
     * @return {void}
     */
    resendOtpCode() : void {
        // Disable button and show loading
        this.$resendCodeButton.setAttribute('disabled', 'true');
        this.$resendCodeButton.querySelector('.loading')?.classList.remove('hidden');

        // Remove errors
        this.$verificationFieldsError.innerHTML = '';
        this.$verificationFieldsError.closest('.form-group')?.classList.remove('has-error');

        this.sendOtpCode().then(response => {
            // Re-enable button and hide loading
            this.$resendCodeButton.removeAttribute('disabled');
            this.$resendCodeButton.querySelector('.loading')?.classList.add('hidden');

            if (!response.success) {
                // Show errors
                const errorMessage = response.data?.textError || this.$phoneFieldForVerification.dataset.invalidFormatMessage;

                this.$verificationFieldsError.innerHTML = `<li>${errorMessage}</li>`;
                this.$verificationFieldsError.closest('.form-group')?.classList.add('has-error');
            }
        }).catch(error => {
            console.error('Error:', error);

            // Re-enable button and hide loading
            this.$resendCodeButton.removeAttribute('disabled');
            this.$resendCodeButton.querySelector('.loading')?.classList.add('hidden');
            
            // Show errors
            this.$verificationFieldsError.innerHTML = `<li>${this.$phoneFieldForVerification.dataset.unexpectedErrorMessage}</li>`;
            this.$verificationFieldsError.closest('.form-group')?.classList.add('has-error');
        });
    }

    /**
     * Validate the OTP code received in phone number
     * 
     * @param {string} code 
     * 
     * @return {Promise<any>}
     */
    async validateOtpCode(code: string) : Promise<any> {
        // Disable button and show loading
        this.$resendCodeButton.setAttribute('disabled', 'true');
        this.$resendCodeButton.querySelector('.loading')?.classList.remove('hidden');

        // Disable fields
        this.$verificationFields.forEach($verificationField => {
            $verificationField.setAttribute('disabled', 'true');
        });

        type ValidateOtpCodeDataType = {
            action: 'validate_otp_code',
            code: string,
            otpGuid: string,
            contactRefExt: string,
        };

        type OldValidateOtpCodeDataType = {
            action: 'old_validate_otp_code',
            code: string,
            otpGuid: string,
            updateContact: 'true'|'false',
        };
        
        const dataToPost: ValidateOtpCodeDataType|OldValidateOtpCodeDataType = this.newVersion ? {
            action: 'validate_otp_code',
            code,
            otpGuid: this.otpGuid as string,
            contactRefExt: this.contactRefExt ?? '',
        } : {
            action: 'old_validate_otp_code',
            code,
            otpGuid: this.otpGuid as string,
            updateContact: (!this.noUpdate).toString() as 'true'|'false',
        };

        return await postData(scriptVar.ajaxUrl, dataToPost).then(response => {
            // Re-enable button and hide loading
            this.$resendCodeButton.removeAttribute('disabled');
            this.$resendCodeButton.querySelector('.loading')?.classList.add('hidden');

            // Re-enable inputs
            this.$verificationFields.forEach($verificationField => {
                $verificationField.removeAttribute('disabled');
            });

            // Hide errors
            this.$verificationFieldsError.innerHTML = '';
            this.$verificationFieldsError.closest('.form-group')?.classList.remove('has-error');
            
            if (response.success) {
                Modal.closeModal(this.$verificationFieldsModal);

                // Show the $phoneFieldContainer
                this.$phoneFieldContainer?.classList.remove('hidden');

                // Reset the $phoneFieldForVerification and $verificationFields inputs
                if (this.resetPhoneFieldOnSuccess) {
                    this.$phoneFieldForVerification.value = '';
                }
                this.$verificationFields.forEach($verificationField => {
                    $verificationField.value = '';
                });

                // Hide the add phone button and show the edit phone button
                this.$addPhoneFieldButton?.classList.add('hidden');
                this.$editPhoneFieldButton?.classList.remove('hidden');

                // Show the submit button if exists
                if (this.$submitButton) {
                    this.$submitButton.classList.remove('hidden');
                }

                // Fill the $otpGuidField input if exists
                if (this.$otpGuidField) {
                    this.$otpGuidField.value = this.otpGuid as string;
                }

                // Fill the $phoneField input if exists
                if (this.$phoneField) {
                    this.$phoneField.value = response.data.phone;
                }

                // Send a custom event
                const event = new CustomEvent('phone_otp_module_success', {
                    detail: {
                        otpGuid: response.data.otpGuid,
                        phone: response.data.phone,
                    },
                });
                this.$container.dispatchEvent(event);

                if (this.autoValidate && this.$submitButton) {
                    this.$submitButton.click();
                }
            } else {
                // Show errors
                const errorMessage = response.data?.textError || this.$phoneFieldForVerification.dataset.wrongCodeMessage;

                this.$verificationFieldsError.innerHTML = `<li>${errorMessage}</li>`;
                this.$verificationFieldsError.closest('.form-group')?.classList.add('has-error');
            }
        }).catch(() => {
            // Re-enable button and hide loading
            this.$resendCodeButton.removeAttribute('disabled');
            this.$resendCodeButton.querySelector('.loading')?.classList.add('hidden');

            // Re-enable fields
            this.$verificationFields.forEach($verificationField => {
                $verificationField.removeAttribute('disabled');
            });

            // Show errors
            this.$verificationFieldsError.innerHTML = `<li>${this.$phoneFieldForVerification.dataset.unexpectedErrorMessage}</li>`;
            this.$verificationFieldsError.closest('.form-group')?.classList.add('has-error');
        });
    }

    /**
     * Format the verification fields inputs
     * 
     * @param {KeyboardEvent} e 
     */
    onVerificationFieldsKeyUp(e: KeyboardEvent) {
        const $target = e.target as HTMLInputElement;
        if ($target.classList.contains('js-phone-otp-module-verification-field')) {
            if ('Backspace' === e.key) {
                const index = parseInt(($target.getAttribute('id') as string).slice(-1));
                const newIndex = index - 1;
                const $previousField = this.$verificationFields[newIndex - 1];
                if (typeof $previousField !== 'undefined') {
                    $previousField.focus();
                }
            } else if (/^[a-zA-Z0-9]$/.test(e.key)) {
                $target.value = e.key.toUpperCase();

                const index = parseInt(($target.getAttribute('id') as string).slice(-1));
                const newIndex = index + 1;
                const $nextField = this.$verificationFields[newIndex - 1];
                if (typeof $nextField !== 'undefined') {
                    $nextField.focus();
                }

                this.checkVerificationFields();
            } else if (/^[a-zA-Z0-9]$/.test($target.value.slice(0, 1))) {
                if (5 === $target.value.length) {
                    const value = $target.value;
                    this.$verificationFields.forEach(($verificationField, index) => {
                        $verificationField.value = value.slice(index, index + 1);
                        if (4 === index) {
                            $verificationField.focus();
                        }
                    });

                    this.checkVerificationFields();
                } else {
                    $target.value = $target.value.slice(0, 1);
                }
            } else {
                $target.value = '';
            }
        }
    }

    /**
     * Verify verification fields inputs and validate the OTP code typped
     * 
     * @return {void}
     */
    checkVerificationFields() : void {
        let fieldsFilled = 0;
        this.$verificationFields.forEach($verificationField => {
            if ('' !== $verificationField.value) {
                fieldsFilled++;
            }
        });

        if (fieldsFilled === this.$verificationFields.length) {
            let code = '';
            this.$verificationFields.forEach($verificationField => {
                code += $verificationField.value;
            });
            this.validateOtpCode(code);
        }
    }
}
